<template>
  <div ref="rootRef" class="dropdown tag-area" :class="{ show: isOpen }">
    <div
      class="tag-area-wrapper vertical-middle"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <div class="tagelements">
        <TagButtonElement
            v-for="(item, index) in selectedTags" :key="index"
            :title="item.title"
            @tagRemove="removeTagFromPool(index)"
            :with-close-button="true"
        />
      </div>
    </div>
    <ul class="dropdown-menu" :class="{ show: isOpen }" id="tagAreaMenu" @click.stop="">
      <div class="dropdown-menu-box animation">
        <div class="dropdown-menu-head">SELECT AN OPTION OR CREATE ONE</div>

        <div class="scrollbar" style="max-height:260px;overflow-y: auto">
          <li v-for="(item, index) in allTags" :key="index">
            <div class="dropdown-item" @click="appendNewTagToPool(index)">
              <TagButtonElement
                  :title="item.title"
                  :class="'btn-grey'"
                  :with-close-button="false"
              />
            </div>
          </li>
        </div>
      </div>
    </ul>
  </div>
</template>
<script setup lang="ts">
import {ref} from 'vue';
import type {CommonNs, DropdownNs} from '@shared/types';
import {useDropdown} from '@shared/composables/use-dropdown';
import {TagButtonElement} from '@shared/components/elements';

const props = withDefaults(defineProps<{
  value: string;
  options: DropdownNs.DropdownOptionsItem[];
  withCloseButton?: boolean;
  dropdownBehaviour?: 'onclick' | 'onhover';
}>(), {
  withCloseButton: false,
  dropdownBehaviour: 'onclick',
});

const emit = defineEmits<{
  (e: 'update:value', value: string): void;
}>();

const {isOpen, rootRef, toggle} = useDropdown();

const selectedTags = ref<CommonNs.IndividualTag[]>(
  props.value.split(',')
    .filter(Boolean)
    .map(item => {
      const option = props.options.find(op => op.value === item);
      return {
        value:  item,
        title:  option?.title || option?.name || item,
        status: 'active' as const,
      };
    }),
);

const allTags = ref<CommonNs.IndividualTag[]>(
  props.options
    .map(item => ({
      title:  item.title ?? '',
      value:  item.value ?? '',
      status: 'active' as const,
    }))
    .filter(x => !selectedTags.value.map(y => y.value).includes(x.value)),
);

function appendNewTagToPool(index: number): void {
  const tag = allTags.value[index];
  if (!tag) return;
  selectedTags.value = [...selectedTags.value, tag];
  allTags.value.splice(index, 1);
  emitValue();
}

function removeTagFromPool(index: number): void {
  const tag = selectedTags.value[index];
  if (!tag) return;
  allTags.value = [...allTags.value, tag];
  selectedTags.value.splice(index, 1);
  emitValue();
}

function emitValue(): void {
  emit(
    'update:value',
    selectedTags.value.map(item => item.value).filter(Boolean).join(','),
  );
}
</script>
<style lang="scss" scoped>

.dropdown-menu {
  min-width: 100%;
  left: 1px !important;

  .dropdown-item {
    justify-content: space-between;

    .dropdown-tags {
      display: flex;
      justify-content: space-between
    }

    .create {
      height: 24px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-right: 5px;
      font-size: var(--fs-14);
    }
  }
}

.tag-area {
  position: relative;
  cursor: pointer;
  width: 100%;

  &:hover {
    .tag-area-wrapper {
      background-color: var(--light-100);
      border: 2px solid var(--light-100);
      box-shadow: var(--shadow-lg) !important;;

      .btn-grey {
        background-color: var(--accent-100);
        border-color: var(--accent-100);
        color: var(--light-100);

        &:hover {
          background-color: var(--accent-200);
          border-color: var(--accent-200);
        }

        .tag-remove {
          display: flex !important;
        }
      }
    }

  }
}

.tag-area-wrapper {
  width: 100%;
  border-radius: $br-sm;
  background-color: var(--light-300);
  border: 2px solid var(--light-300);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: 40px;

  .tagelements {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    font-size: var(--fs-14);
    padding: 6px 10px;
  }

  .tag {
    .tag-remove {
      display: none !important;
    }
  }

  &:hover {
    background-color: var(--light-100);
    border: 2px solid var(--light-100);
    box-shadow: var(--shadow-lg) !important;

    .btn-grey {
      background-color: var(--accent-100);
      border-color: var(--accent-100);
      color: var(--light-100);

      &:hover {
        background-color: var(--accent-200);
        border-color: var(--accent-200);
      }

      .tag-remove {
        display: flex !important;
      }
    }
  }

  &:focus,
  &:active {
    background-color: var(--light-100);
    border: 2px solid var(--light-100);
    box-shadow: var(--shadow-lg) !important;

    .btn-grey {
      background-color: var(--accent-100);
      border-color: var(--accent-100);
      color: var(--light-100);

      &:hover {
        background-color: var(--accent-200);
        border-color: var(--accent-200);
      }
    }
  }
}

.tag-search-input {
  padding: 0.375rem 0.75rem;
  border: none;
  background: transparent;
  width: 100%;
  height: 36px;
  font-size: var(--fs-14);

  &::-webkit-input-placeholder { /* Edge */
    color: var(--text-neutral);
  }

  &:-ms-input-placeholder { /* Internet Explorer 10-11 */
    color: var(--text-neutral);
  }

  &::placeholder {
    color: var(--text-neutral);
  }

  &:focus {
    outline: none;
  }
}

.tag-area-dropdown {
  position: absolute;
  z-index: 10;
  margin-top: 1px;
  background: #ffffff;
  width: 100%;
  padding: 10px;
  color: var(--text-neutral);
  box-shadow: var(--shadow-lg);
  border-radius: $br-sm;

  & > div {
    margin: var(--space-0);
  }

  .dropdown-heading {
    font-size: var(--fs-12);
  }

  .dropdown-footer {
    font-size: var(--fs-14);
  }
}

</style>
